import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";

/**
 * "Install the app" button for desktop and Android browsers.
 *
 * Browsers only allow an install prompt when the site is installable
 * (manifest + icons + offline support — all already in place) and fire
 * `beforeinstallprompt` when they are ready to offer it. We show the button
 * only then; on browsers that never fire it (Safari) the button stays hidden
 * and members can install via the browser menu instead.
 *
 * The captured prompt lives in a module-level store rather than in one
 * component's state, because the event fires once, early, while some
 * placements (the phone menu) only mount later.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();
let listenerInstalled = false;

function notify() {
  subscribers.forEach((fn) => fn());
}

function ensureListener() {
  if (listenerInstalled || typeof window === "undefined") return;
  listenerInstalled = true;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

function useInstallPrompt(): BeforeInstallPromptEvent | null {
  const [, setVersion] = useState(0);
  useEffect(() => {
    ensureListener();
    const bump = () => setVersion((v) => v + 1);
    subscribers.add(bump);
    // The prompt may have arrived before this component mounted (the phone
    // menu opens later), so re-read the store on mount.
    if (deferredPrompt) bump();
    return () => {
      subscribers.delete(bump);
    };
  }, []);
  return deferredPrompt;
}

function isInstalled(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari sets navigator.standalone when launched from the home screen.
  return Boolean((navigator as unknown as { standalone?: boolean }).standalone);
}

export function InstallAppButton({
  className,
  label = "Install the Recovery Trauma™ app",
  iconClassName = "h-4 w-4",
  title,
}: {
  className?: string;
  label?: string;
  iconClassName?: string;
  /** Tooltip / accessible name when the label is visually hidden. */
  title?: string;
}) {
  const promptEvent = useInstallPrompt();
  const [installed, setInstalled] = useState(isInstalled);
  // Never render during server render or the first client paint, so the
  // server HTML and the hydrated HTML always agree — the install offer can
  // arrive at any moment, including mid-hydration.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted || installed) return null;

  const install = async () => {
    if (!promptEvent) {
      // The browser hasn't offered an install prompt (Safari never does, and
      // Chrome/Edge only offer it on the published site, not the preview).
      // Explain how to install instead of silently doing nothing.
      toast.info("Install Recovery Trauma™ on this device", {
        description:
          "On a computer: open recoverytrauma.com in Chrome or Edge and click the install icon in the address bar. On a Mac in Safari: File → Add to Dock. On a phone: use your browser menu → Add to Home Screen.",
        duration: 12000,
      });
      return;
    }
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      // Only drop the offer once it actually worked — a member who closed the
      // browser's install dialog by mistake can try the button again.
      if (choice.outcome === "accepted") {
        deferredPrompt = null;
        notify();
        setInstalled(true);
      }
    } catch {
      /* user dismissed or browser declined — nothing to do */
    }
  };

  return (
    <button
      type="button"
      onClick={install}
      title={title}
      aria-label={title}
      className={
        className ??
        "inline-flex items-center gap-2 text-[15px] font-medium text-[var(--gold-deep)] underline-offset-2 transition-colors hover:underline"
      }
    >
      <Download className={iconClassName} aria-hidden />
      <span data-no-translate>{label}</span>
    </button>
  );
}
